/**
 * Chunk Load Error Handler
 *
 * Handles webpack chunk loading errors by implementing retry logic
 * and providing fallback behavior for failed chunk loads.
 */

import { doNotifyError } from "./app-notification";

export interface ChunkRetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  onRetry?: (error: Error, retryCount: number) => void;
  onFailure?: (error: Error) => void;
}

const CHUNK_RELOAD_KEY = "chunkLoadRetryCount";
const MAX_CHUNK_RELOAD_RETRIES = 2;
const CHUNK_RELOAD_DELAY_MS = 2000;

let reloadScheduled = false;

export function isChunkLoadError(error: Error | null | undefined): boolean {
  return (
    !!error &&
    !!error.message &&
    (error.message.indexOf("Loading chunk") !== -1 ||
      error.message.indexOf("Loading CSS chunk") !== -1 ||
      error.name === "ChunkLoadError")
  );
}

export function resetChunkLoadRetries(): void {
  sessionStorage.removeItem(CHUNK_RELOAD_KEY);
}

export function handleChunkLoadError(error: Error): void {
  if (reloadScheduled) return;

  const stored = parseInt(sessionStorage.getItem(CHUNK_RELOAD_KEY) || "0", 10);
  const retries = Number.isNaN(stored) ? 0 : stored;

  if (retries < MAX_CHUNK_RELOAD_RETRIES) {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(retries + 1));
    reloadScheduled = true;
    console.warn(
      `Chunk load failed (attempt ${retries +
        1}/${MAX_CHUNK_RELOAD_RETRIES}); reloading…`,
      error.message
    );
    setTimeout(() => {
      window.location.reload();
    }, CHUNK_RELOAD_DELAY_MS);
  } else {
    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
    console.error("Chunk load failed after max retries:", error.message);
    doNotifyError(
      "We couldn't load part of the application. Please refresh the page or try again shortly.",
      "Update unavailable",
      0
    );
  }
}

/**
 * Retry a failed dynamic import with exponential backoff
 * Note: Simplified version to avoid TypeScript Promise constructor issues
 */
export function retryChunkLoad<T>(
  importFn: () => Promise<T>,
  options: ChunkRetryOptions = {}
): Promise<T> {
  const { maxRetries = 3, retryDelay = 1000, onRetry, onFailure } = options;

  let retryCount = 0;

  function attempt(): Promise<T> {
    return importFn().catch((error: Error) => {
      if (!isChunkLoadError(error) || retryCount >= maxRetries) {
        if (onFailure) {
          onFailure(error);
        }
        throw error;
      }

      retryCount++;

      if (onRetry) {
        onRetry(error, retryCount);
      }

      // Wait for retryDelay milliseconds before re-attempting
      console.warn(
        `Retrying chunk load (attempt ${retryCount}/${maxRetries}) after ${retryDelay}ms:`,
        error.message
      );
      return new Promise<T>(resolve =>
        setTimeout(resolve, retryDelay)
      ).then(() => attempt());
    });
  }

  return attempt();
}

/**
 * Wrap a dynamic import with retry logic
 */
export function withChunkRetry<T>(
  importFn: () => Promise<T>,
  options?: ChunkRetryOptions
): () => Promise<T> {
  return () => retryChunkLoad(importFn, options);
}

/**
 * Set up global chunk loading error handler
 */
export function setupGlobalChunkErrorHandler(): void {
  window.addEventListener("unhandledrejection", event => {
    const reason = event.reason;
    if (!(reason instanceof Error)) return;
    if (isChunkLoadError(reason)) {
      handleChunkLoadError(reason);
    }
  });
}
